import { SxProps, Theme } from '@mui/material';
import { brandAlpha, hoverOnly } from '../../../theme/mixins';

/** The value and, when something is queued, the chip that says so. */
export const triggerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
};

/**
 * The value, which is also the way in.
 *
 * A button that reads as text: no fill, no outline, ordinary body colour, and the pencil beside
 * it doing the work of saying it can be clicked. The tint arrives on hover, so the row still
 * responds without a box drawn in every one of them at rest.
 *
 * **The pencil is always visible, not hover-only**, and that follows a rule this app already
 * wrote down. `MembershipStatusChip` is its one actionable badge, and its docstring settles the
 * same question: something that reads as output needs its affordance *on* it, "rather than
 * assumed". Hover-only would also be nothing at all on the tablet this console is used from.
 */
export const triggerStyles: SxProps<Theme> = {
    color: 'text.primary',
    fontWeight: 500,
    minWidth: 0,
    px: 0.75,
    py: 0.25,
    textTransform: 'none',
    ...hoverOnly({ backgroundColor: brandAlpha('primary', 0.08) }),
};

/** Muted, because it qualifies the value rather than competing with it. */
export const triggerIconStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: 15,
    ml: 0.5,
};

/** A therapist whose access has lapsed keeps their rate on screen, read-only. */
export const readOnlyValueStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

/**
 * The queued-change flag.
 *
 * A chip rather than the date in words: free text in this column set the column's width and the
 * row's height, twice. A chip is a small fixed object, and the date it stands for is one click
 * away in the editor that put it there.
 */
export const scheduledChipStyles: SxProps<Theme> = {
    borderColor: brandAlpha('primary', 0.4),
    color: 'text.secondary',
    height: 22,
    '& .MuiChip-icon': { fontSize: 14, color: 'text.secondary' },
};

// ------------------------------------------------------------------ the editor

/** Room to say things properly, which is the point of it not being a table cell. */
export const popoverPaperStyles: SxProps<Theme> = {
    borderRadius: 3,
    // Never wider than the phone it may open on, and never so narrow that the effective-date
    // sentence wraps to four lines.
    width: { xs: 'calc(100vw - 32px)', sm: 320 },
    maxWidth: 360,
    p: 2.5,
};

export const popoverTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    mb: 1.5,
};

export const fieldRowStyles: SxProps<Theme> = {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 1,
};

export const fieldStyles: SxProps<Theme> = {
    width: 120,
    // The spinner arrows are suppressed on both engines: `type="number"` is kept for the numeric
    // keypad it raises on a phone and for the bounds it carries, not for its stepper.
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};

/** Sits on the field's own baseline rather than the row's top. */
export const suffixStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mt: 1.25,
};

/**
 * The sentence the whole month-boundary rule rests on.
 *
 * Said here, at the moment of the decision, as well as in the footnote under the table and in the
 * snackbar after saving. Saving deliberately does not move the current month's figures, so an
 * editor that stayed quiet about it would look like one that had failed.
 */
export const explanationStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 1.5,
};

export const formAlertStyles: SxProps<Theme> = {
    mb: 2,
};

export const blockedSaveStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 1.5,
};

/** Remove sits away from Cancel and Save: it is a different kind of answer. */
export const actionsRowStyles: SxProps<Theme> = {
    alignItems: 'center',
    display: 'flex',
    gap: 1,
    justifyContent: 'space-between',
    mt: 2.5,
};

export const confirmActionsStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 1,
};

/** While the price list and the therapist's disciplines are on their way. */
export const loadingStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 2,
};

export const disciplinesTitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 2,
};

/** One discipline's override, stacked under the default. */
export const disciplineRowStyles: SxProps<Theme> = {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 1,
    mt: 1.5,
    // A discipline's name is the clinic's own and can be long; the label shrinks above the field
    // and must not be cut to the field's width.
    '& .MuiInputLabel-root': { maxWidth: 'none' },
};

export const disciplinesHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 1,
};
